import { useCallback, useState } from 'react';
import useSWR from 'swr';

import { AgentMessageStatus } from '@commenda/types';
import { SWRFetcher } from '@commenda/utils';

import type {
  IFilingsAuditorConversationView,
  IFilingsAuditorRunResponse,
} from './types';

const POLL_INTERVAL_MS = 2000;

const IN_PROGRESS_STATUSES: ReadonlySet<AgentMessageStatus> = new Set([
  AgentMessageStatus.PENDING,
  AgentMessageStatus.STREAMING,
  AgentMessageStatus.EXECUTING,
  AgentMessageStatus.AWAITING_CONFIRMATION,
]);

function isRunInFlight(
  conversation: IFilingsAuditorConversationView | null
): boolean {
  const last = conversation?.messages?.at(-1);
  if (!last) return false;
  return IN_PROGRESS_STATUSES.has(last.status);
}

export function useFilingsAuditorRunPolling(conversationId: string | null) {
  const [optimisticProcessing, setOptimisticProcessing] = useState(false);

  const runResponse = useSWR<IFilingsAuditorRunResponse>(
    conversationId
      ? `agent-platform/filings-auditor/runs/${conversationId}`
      : null,
    SWRFetcher({ method: 'GET' }),
    {
      refreshInterval: (data) =>
        optimisticProcessing || isRunInFlight(data?.run.conversation ?? null)
          ? POLL_INTERVAL_MS
          : 0,
      onSuccess: (data) => {
        if (!isRunInFlight(data?.run.conversation ?? null))
          setOptimisticProcessing(false);
      },
    }
  );

  const conversation = runResponse.data?.run.conversation ?? null;
  const result = runResponse.data?.run.result ?? null;

  const startProcessing = useCallback(() => {
    setOptimisticProcessing(true);
    runResponse.mutate().catch(console.error);
  }, [runResponse]);

  return {
    conversation,
    result,
    isProcessing: optimisticProcessing || isRunInFlight(conversation),
    isLoading: runResponse.isLoading,
    error: runResponse.error,
    startProcessing,
    mutate: runResponse.mutate,
  };
}
